import { useMutation, useQuery } from "@tanstack/react-query";

import { queryClient } from "@/api/core";
import { useApi } from "@/hooks";
import { type CarouselGroup, carouselGroupSchema } from "@/schemas";

interface Params {
  group_id: string;
}

const CarouselGroupKeys = {
  groups: ["carousels", "groups"] as const,
};

function useCarouselGroups() {
  const api = useApi();

  const { data, ...rest } = useQuery<CarouselGroup[]>({
    queryKey: CarouselGroupKeys.groups,
    queryFn: async () => {
      const { data } = await api.get<CarouselGroup[]>(
        "/api/v1/truth/carousels/groups",
      );

      return data.map((item) => carouselGroupSchema.parse(item));
    },
    keepPreviousData: true,
    placeholderData: [],
  });

  return {
    ...rest,
    groups: data || [],
  };
}

function useMarkCarouselGroupAsSeen() {
  const api = useApi();

  return useMutation({
    mutationFn: (params: Params) =>
      api.post("/api/v1/truth/carousels/groups/seen", params),
    async onMutate(variables) {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: CarouselGroupKeys.groups });

      // Snapshot the previous value
      const prevCarouselGroups =
        queryClient.getQueryData<CarouselGroup[]>(CarouselGroupKeys.groups) ||
        [];
      const nextCarouselGroups = prevCarouselGroups.map((prev) => ({
        ...prev,
        seen: prev.group_id === variables.group_id ? true : prev.seen,
      }));

      // Optimistically update to the new value
      queryClient.setQueryData(CarouselGroupKeys.groups, nextCarouselGroups);

      // Return a context object with the snapshotted value
      return { prevCarouselGroups };
    },
    onError(_error, _variables, context) {
      // Optimistically update to the new value
      queryClient.setQueryData(
        CarouselGroupKeys.groups,
        context?.prevCarouselGroups,
      );
    },
  });
}

async function pushCarouselGroupToEndOfList(groupId: string) {
  // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
  await queryClient.cancelQueries({ queryKey: CarouselGroupKeys.groups });

  const prevCarouselGroups =
    queryClient.getQueryData<CarouselGroup[]>(CarouselGroupKeys.groups) || [];
  const carouselGroup = prevCarouselGroups.find(
    (group) => group.group_id === groupId,
  );
  const nextCarouselGroups = [
    ...prevCarouselGroups.filter((group) => group.group_id !== groupId),
    carouselGroup,
  ];

  queryClient.setQueryData(CarouselGroupKeys.groups, nextCarouselGroups);
}

export {
  useCarouselGroups,
  useMarkCarouselGroupAsSeen,
  pushCarouselGroupToEndOfList,
  CarouselGroupKeys,
};
